import { Shimmer } from "@/components/ai-elements/shimmer";
import { ChatElapsedTime } from "@/components/chat/chat-dialog-primitives";
import { ChatResearchReadPages } from "@/components/chat/chat-research-read-pages";
import { ResearchDomainsRow } from "@/components/chat/research-domains-row";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useStreamdownTranslations } from "@/hooks/useStreamdownTranslations";
import { useTranslation } from "@/hooks/useTranslation";
import {
  isStreamedPart,
  isToolPartRunning,
  type AssistantPart,
} from "@/lib/chat-render-items";
import {
  getRegistryOutput,
  getScrapedUrl,
  getToolCriteria,
  getWebSearchQueries,
  getWebSearchSources,
  hasStepDetail,
  type ThinkingStep,
  type ThinkingStepStatus,
} from "@/lib/chat-thinking-trace";
import {
  COLLAPSE_ANIMATION_CLASS,
  countSegments,
  shownSeconds,
  type CounterSegment,
  type RowTime,
} from "@/lib/chat-thinking-format";
import { useKeptDomains } from "@/hooks/useKeptDomains";
import { sourceLink } from "@/lib/source-link";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/utils/time";
import type { ToolUIPart } from "ai";
import {
  Building2Icon,
  CheckIcon,
  ChevronDownIcon,
  GlobeIcon,
  LinkIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState, type ReactNode } from "react";
import { Streamdown } from "streamdown";

/** Favicons under a search row; the rest are counted in a "+N". */
const ROW_MAX_FAVICONS = 5;
/** Sites named under a reading step; the rest are counted, not listed. */
const MAX_LISTED_DOMAINS = 3;

/**
 * Counters joined by " · ", each sliding up into place when its number
 * changes, so the progress reads as it climbs rather than flickering. Keyed by
 * what they count: a counter appearing in front does not replay the others.
 */
export function RollingSegments({
  segments,
}: {
  segments: readonly CounterSegment[];
}) {
  const shouldReduceMotion = useReducedMotion();
  return (
    <>
      {segments.map(({ key, text }, index) => (
        <span key={key} className="inline-flex overflow-hidden align-bottom">
          {index > 0 && <span aria-hidden>&nbsp;·&nbsp;</span>}
          <AnimatePresence initial={false} mode="popLayout">
            <motion.span
              key={text}
              initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {text}
            </motion.span>
          </AnimatePresence>
        </span>
      ))}
    </>
  );
}

/**
 * A line of a row that opens its height as it arrives — counters, favicons,
 * sites — so the rows under it slide down instead of jumping.
 */
function GrowIn({ show, children }: { show: boolean; children: ReactNode }) {
  const shouldReduceMotion = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          className="overflow-hidden"
          initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StepIcon({
  status,
  isLive,
}: {
  status: ThinkingStepStatus;
  /** Only a step seen finishing pops its check; a reloaded one just shows it. */
  isLive: boolean;
}) {
  const shouldReduceMotion = useReducedMotion();
  if (status === "running") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full bg-majorelle/10">
        <span className="size-2 rounded-full bg-majorelle animate-pulse motion-reduce:animate-none" />
      </span>
    );
  }
  if (status === "done") {
    return (
      <motion.span
        className="flex size-5 items-center justify-center rounded-full bg-majorelle text-majorelle-foreground"
        initial={isLive && !shouldReduceMotion ? { scale: 0.6, opacity: 0 } : false}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <CheckIcon className="size-3" strokeWidth={3} />
      </motion.span>
    );
  }
  return (
    <span
      className={cn(
        "flex size-5 items-center justify-center rounded-full border",
        status === "error" ? "border-red-600/40 text-red-600" : "text-muted-foreground"
      )}
    >
      <XIcon className="size-3" strokeWidth={3} />
    </span>
  );
}

function SourceAnchor({ url, children }: { url: string; children: ReactNode }) {
  const link = sourceLink(url);
  const className =
    "flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground";
  // The URL comes from a page the model read: anything but http(s) is shown,
  // never linked.
  return link ? (
    <a href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  ) : (
    <div className={className}>{children}</div>
  );
}

/** "jobTitles" → "job titles": the input's own field name, readable. */
function readableKey(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

/**
 * What a tool was asked for — the company, the role, the town — in the
 * model's own words and field names: the detail of a row whose results show in
 * the tool's own card. Only the fields the tool lists as criteria.
 */
function ToolCriteria({ part }: { part: AssistantPart }) {
  const fields = getToolCriteria(part).map(
    ([field, value]) => [readableKey(field), value] as const
  );
  if (fields.length === 0) return null;
  return (
    <dl className="space-y-0.5 text-xs text-muted-foreground">
      {fields.map(([key, value]) => (
        <div key={key} className="flex gap-1.5">
          <dt className="shrink-0 text-muted-foreground/70">{key} :</dt>
          <dd className="min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function RegistryDetail({ part, keyPrefix }: { part: AssistantPart; keyPrefix: string }) {
  const { t } = useTranslation();
  const output = getRegistryOutput(part);
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Building2Icon className="size-3 shrink-0" />
        <span className="truncate">
          {output?.criteria
            ? t("chat.message.research.registryCriteria", {
                criteria: output.criteria,
                count: output.totalFound,
              })
            : t("chat.message.tool.searchCompanyRegistry")}
        </span>
      </div>
      {(output?.companies ?? []).map((company) => (
        <SourceAnchor key={`${keyPrefix}-${company.siren}`} url={company.profileUrl}>
          <span className="truncate underline-offset-2 hover:underline">{company.name}</span>
          <span className="shrink-0 text-muted-foreground/60">
            {[
              company.siret && `SIRET ${company.siret}`,
              company.nafCode && `NAF ${company.nafCode}`,
              company.city &&
                (company.postalCode ? `${company.city} (${company.postalCode})` : company.city),
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </SourceAnchor>
      ))}
    </div>
  );
}

function WebSearchDetail({ part, keyPrefix }: { part: AssistantPart; keyPrefix: string }) {
  const { t } = useTranslation();
  const input = (part as ToolUIPart).input as { objective?: string; query?: string } | undefined;
  const objective = input?.objective ?? input?.query;
  const sources = getWebSearchSources(part);
  return (
    <div className="space-y-1">
      {objective && <div className="text-xs font-medium text-foreground/80">{objective}</div>}
      {getWebSearchQueries(part).map((query, queryIndex) => (
        <div
          key={`${keyPrefix}-query-${queryIndex}`}
          className="flex items-center gap-2 text-xs text-muted-foreground"
        >
          <SearchIcon className="size-3 shrink-0" />
          <span className="truncate">{query}</span>
        </div>
      ))}
      {sources.length > 0 && (
        <div className="space-y-1 pt-1.5">
          <div className="text-xs font-medium text-foreground/80">
            {t("chat.message.research.sourcesFound", { count: sources.length })}
          </div>
          {sources.map((source) => (
            <SourceAnchor key={`${keyPrefix}-${source.url}`} url={source.url}>
              <GlobeIcon className="size-3 shrink-0" />
              <span className="truncate underline-offset-2 hover:underline">
                {source.title || source.url}
              </span>
              <span className="shrink-0 text-muted-foreground/60">
                {sourceLink(source.url)?.host}
              </span>
            </SourceAnchor>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What a step did, in the words of whoever wrote it — the model's reasoning,
 * its queries, what a tool was asked for, the pages and their titles. Only
 * shown on the reader's request.
 */
function StepDetail({ step, messageId, isLive }: { step: ThinkingStep; messageId: string; isLive: boolean }) {
  const streamdownTranslations = useStreamdownTranslations();
  return (
    // Focusable, so a keyboard can scroll a long detail.
    <div
      tabIndex={0}
      className="mt-1.5 max-h-60 space-y-2 overflow-y-auto overscroll-y-contain rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {step.parts.map(({ part, index }) => {
        if (!isStreamedPart(part)) return null;
        const key = `${messageId}-detail-${index}`;
        if (part.type === "reasoning") {
          return (
            <div key={key} className="border-l-2 border-border pl-3 text-xs text-muted-foreground">
              <Streamdown translations={streamdownTranslations}>{part.text}</Streamdown>
            </div>
          );
        }
        if (part.type === "tool-webSearch") {
          return <WebSearchDetail key={key} part={part} keyPrefix={key} />;
        }
        if (part.type === "tool-searchCompanyRegistry") {
          return <RegistryDetail key={key} part={part} keyPrefix={key} />;
        }
        if (part.type === "tool-readPages") {
          return (
            <ChatResearchReadPages
              key={key}
              part={part as ToolUIPart}
              isRunning={isLive && isToolPartRunning(part as ToolUIPart)}
            />
          );
        }
        if (part.type === "tool-scrapeWebsite") {
          const url = getScrapedUrl(part);
          return url ? (
            <SourceAnchor key={key} url={url}>
              <LinkIcon className="size-3 shrink-0" />
              <span className="truncate underline-offset-2 hover:underline">{url}</span>
            </SourceAnchor>
          ) : null;
        }
        return <ToolCriteria key={key} part={part} />;
      })}
    </div>
  );
}

/** One step of the timeline: its state, its name, its time, what it counted. */
export function StepRow({
  step,
  time,
  messageId,
  isLive,
  isLast,
  liveLabel,
  isTraceRunning,
}: {
  step: ThinkingStep;
  time: RowTime | undefined;
  messageId: string;
  isLive: boolean;
  isLast: boolean;
  /** What the running step is on, when the call says more than its name. */
  liveLabel?: string;
  isTraceRunning: boolean;
}) {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const label = t(`chat.thinkingTrace.steps.${step.kind}`);
  const segments = [
    ...(liveLabel ? [{ key: "live", text: liveLabel }] : []),
    ...countSegments(step.counts, t, isTraceRunning),
  ];
  // A settled call no longer carries the sites its snapshots named: the row
  // keeps the ones it showed, so its favicons do not vanish as it ends.
  const rowDomains = useKeptDomains(step.domains);
  const listedDomains = step.domains.slice(0, MAX_LISTED_DOMAINS);
  const hiddenDomainCount = step.domains.length - listedDomains.length;
  const hasDetail = hasStepDetail(step);
  const seconds = time ? shownSeconds(time.finishedMs) : 0;
  const isFailed = step.status === "error" || step.status === "interrupted";

  return (
    <motion.li
      className="relative flex gap-2.5 overflow-hidden"
      // The row opens its own height before it shows: what sits under the
      // card slides down with it instead of jumping by a row.
      initial={
        shouldReduceMotion ? { opacity: 0 } : { opacity: 0, height: 0, y: 6, filter: "blur(4px)" }
      }
      animate={{ opacity: 1, height: "auto", y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className="relative shrink-0">
        <StepIcon status={step.status} isLive={isLive} />
        {!isLast && (
          // The line down to the next step fills as this one finishes: the
          // progress reads at a glance, top to bottom.
          <div className="absolute top-6 -bottom-1 left-1/2 w-px -translate-x-1/2 overflow-hidden bg-border">
            <motion.div
              className="size-full origin-top bg-majorelle/60"
              initial={
                isLive && !shouldReduceMotion
                  ? { scaleY: 0 }
                  : { scaleY: step.status === "running" ? 0 : 1 }
              }
              animate={{ scaleY: step.status === "running" ? 0 : 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </div>
        )}
      </div>
      <Collapsible open={isDetailOpen} onOpenChange={setIsDetailOpen} className="min-w-0 flex-1 pb-3">
        <div className="flex min-h-5 items-center gap-2 text-sm">
          {step.status === "running" ? (
            <Shimmer className="min-w-0 truncate font-medium">{label}</Shimmer>
          ) : (
            <span className="min-w-0 truncate text-foreground">{label}</span>
          )}
          {/* Right after the name, so the eye reads a step in one go — a
              column at the far edge sent it back and forth on every row. */}
          {time?.runningSince !== undefined ? (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground/70">
              <ChatElapsedTime startedAtMs={time.runningSince - time.finishedMs} />
            </span>
          ) : (
            seconds > 0 && (
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground/70">
                {formatDuration(seconds)}
              </span>
            )
          )}
          {/* A failed or cut step says so in words, not by its colour alone. */}
          {isFailed ? (
            <span className={cn("shrink-0 text-xs", step.status === "error" ? "text-red-600" : "text-muted-foreground")}>
              {t(`chat.thinkingTrace.status.${step.status}`)}
            </span>
          ) : (
            <span className="sr-only">{t(`chat.thinkingTrace.status.${step.status}`)}</span>
          )}
          {hasDetail && (
            // On the name's line: a button on a line of its own grew the row
            // by its height the moment the model's text arrived.
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                aria-label={t(
                  isDetailOpen ? "chat.thinkingTrace.hideDetailOf" : "chat.thinkingTrace.showDetailOf",
                  { step: label }
                )}
                className="relative ml-auto h-5 shrink-0 px-1.5 text-muted-foreground after:absolute after:-inset-x-1 after:-inset-y-3 after:content-['']"
              >
                {t(isDetailOpen ? "chat.thinkingTrace.hideDetail" : "chat.thinkingTrace.showDetail")}
                <ChevronDownIcon
                  className={cn(
                    "transition-transform duration-200 motion-reduce:transition-none",
                    isDetailOpen && "rotate-180"
                  )}
                />
              </Button>
            </CollapsibleTrigger>
          )}
        </div>
        <GrowIn show={Boolean(step.tacticTitles?.length)}>
          <p className="truncate text-xs text-muted-foreground">
            {step.tacticTitles?.join(" · ")}
          </p>
        </GrowIn>
        <GrowIn show={Boolean(step.narration)}>
          <p className="line-clamp-2 text-xs text-muted-foreground text-pretty">{step.narration}</p>
        </GrowIn>
        <GrowIn show={segments.length > 0}>
          <div className="text-xs tabular-nums text-muted-foreground">
            <RollingSegments segments={segments} />
          </div>
        </GrowIn>
        <GrowIn show={step.kind === "webSearch" && rowDomains.length > 0}>
          <ResearchDomainsRow
            domains={rowDomains}
            isRunning={step.status === "running"}
            maxVisible={ROW_MAX_FAVICONS}
            className="px-0 pt-1 pb-0 pl-0"
          />
        </GrowIn>
        <GrowIn show={listedDomains.length > 0 && step.kind === "readPages"}>
          <div className="truncate text-xs text-muted-foreground/70">
            {listedDomains.join(" · ")}
            {hiddenDomainCount > 0 && ` · +${hiddenDomainCount}`}
          </div>
        </GrowIn>
        {hasDetail && (
          <CollapsibleContent className={COLLAPSE_ANIMATION_CLASS}>
            <StepDetail step={step} messageId={messageId} isLive={isLive} />
          </CollapsibleContent>
        )}
      </Collapsible>
    </motion.li>
  );
}
